import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { WaitlistCounter } from '@/components/site/WaitlistCounter';
import { ConceptCard } from '@/components/site/ConceptCard';
import { VERTICALS, VERTICALS_BY_CODE, verticalCopy } from '@/lib/verticals';
import { generateConcept, unlockConcept, fetchShowcases } from '@/lib/api';
import { createApp } from '@/lib/apps-api';
import { useAuth } from '@/lib/auth';
import { track } from '@/lib/analytics';
import { setPageMeta } from '@/lib/seo';
import { useT, useRaw, useLang } from '@/lib/i18n';
import { toast } from 'sonner';
import {
  Sparkles,
  ArrowRight,
  Lock,
  RefreshCw,
  Check,
  LayoutTemplate,
} from 'lucide-react';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const LOADING_MS = [0, 2500, 5000, 8000];

const MAX_LEN = 1000;
const MIN_LEN = 10;

export default function Generator() {
  const t = useT();
  const raw = useRaw();
  const { lang } = useLang();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState('form');
  const [vertical, setVertical] = useState('sante');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [loadingIdx, setLoadingIdx] = useState(0);
  const [teaser, setTeaser] = useState(null);
  const [fullConcept, setFullConcept] = useState(null);
  const [genError, setGenError] = useState('');
  const [openingBuilder, setOpeningBuilder] = useState(false);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [unlocking, setUnlocking] = useState(false);

  const [samples, setSamples] = useState([]);

  const loadingSteps = raw('generator.loading_steps') || [];
  const placeholders = raw('generator.placeholders') || {};

  useEffect(() => {
    setPageMeta({
      title: t('seo.generator_title'),
      description: t('generator.seo_description'),
      path: '/generateur',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  // Analytics : vue de la page générateur (une fois par mount)
  useEffect(() => {
    track('generator_view', { lang });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Analytics : quand le teaser (step=result) apparaît, on émet unlock_shown
  useEffect(() => {
    if (step === 'result' && teaser?.id) {
      track('unlock_shown', { lang, vertical, concept_id: teaser.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, teaser]);

  useEffect(() => {
    (async () => {
      const items = await fetchShowcases({ vertical: undefined, limit: 3, lang });
      setSamples(items);
    })();
  }, [lang]);

  useEffect(() => {
    if (step !== 'loading') return;
    setLoadingIdx(0);
    const timers = LOADING_MS.slice(1).map((ms, i) =>
      setTimeout(() => setLoadingIdx(i + 1), ms),
    );
    return () => timers.forEach(clearTimeout);
  }, [step]);

  const currentVertical = VERTICALS_BY_CODE[vertical];
  const currentVerticalCopy = verticalCopy(raw, vertical);

  const validateForm = () => {
    const e = {};
    if (!VERTICALS_BY_CODE[vertical]) e.vertical = t('generator.validation.universe_required');
    const d = description.trim();
    if (d.length < MIN_LEN) e.description = t('generator.validation.desc_min', { min: MIN_LEN });
    else if (d.length > MAX_LEN) e.description = t('generator.validation.desc_max', { max: MAX_LEN });
    return e;
  };

  const handleGenerate = async (ev) => {
    ev?.preventDefault?.();
    const e = validateForm();
    setErrors(e);
    if (Object.keys(e).length) return;

    setStep('loading');
    setGenError('');
    setTeaser(null);
    setFullConcept(null);
    track('generate_started', { lang, vertical });

    const res = await generateConcept({ vertical, description: description.trim(), lang });
    if (!res.ok) {
      const msg = t(`errors.${res.code || 'generate_generic'}`);
      setGenError(msg);
      setStep('form');
      track('generate_failed', { lang, vertical });
      toast.error(msg, { duration: 6000 });
      return;
    }
    setTeaser(res.teaser);
    setStep('result');
    track('generate_success', { lang, vertical, concept_id: res.teaser?.id });
  };

  const handleUnlock = async (ev) => {
    ev?.preventDefault?.();
    if (unlocking) return;
    setEmailError('');
    const v = email.trim();
    if (!v) return setEmailError(t('errors.email_required'));
    if (v.length > 254 || !EMAIL_RE.test(v))
      return setEmailError(t('errors.email_invalid'));
    setUnlocking(true);
    track('unlock_submitted', { lang, vertical, concept_id: teaser?.id });
    const res = await unlockConcept(teaser.id, v);
    setUnlocking(false);
    if (!res.ok) {
      const msg = t(`errors.${res.code || 'unlock_generic'}`);
      setEmailError(msg);
      toast.error(msg, { duration: 5000 });
      return;
    }
    setFullConcept(res.concept);
    track('unlock_success', { lang, vertical, concept_id: teaser?.id });
    if (res.alreadyIn) {
      toast(t('generator.unlock_already_in'), { icon: '✨', duration: 4000 });
    } else {
      toast.success(t('generator.unlock_success'), { duration: 5000 });
    }
  };

  const handleReset = () => {
    setStep('form');
    setErrors({});
    setTeaser(null);
    setFullConcept(null);
    setEmail('');
    setEmailError('');
    setGenError('');
    setDescription('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remaining = MAX_LEN - description.length;

  return (
    <div data-testid="page-generator">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-10">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="om-anim-halo absolute left-[8%] -top-24 h-[380px] w-[380px] rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(139,92,246,0.28), transparent 70%)' }}
            />
            <div
              className="om-anim-halo absolute right-[6%] top-[15%] h-[420px] w-[420px] rounded-full blur-3xl om-d-300"
              style={{ background: 'radial-gradient(closest-side, rgba(16,185,129,0.22), transparent 70%)' }}
            />
          </div>

          <div className="om-container">
            <div className="mx-auto max-w-3xl text-center">
              <span className="om-eyebrow" data-testid="generator-eyebrow">
                <Sparkles size={12} strokeWidth={2.6} /> {t('generator.eyebrow')}
              </span>
              <h1 className="om-h1 mt-6 text-[42px] sm:text-[56px] lg:text-[64px] text-[var(--om-ink)]" data-testid="generator-title">
                {t('generator.title_l1')}<br />
                <span
                  style={{
                    backgroundImage:
                      'linear-gradient(120deg, #10B981 0%, #8B5CF6 50%, #F97316 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  {t('generator.title_l2')}
                </span>
              </h1>
              <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg leading-relaxed text-[var(--om-muted)]">
                {t('generator.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {step === 'form' && (
          <section className="pb-20">
            <div className="om-container">
              <form
                onSubmit={handleGenerate}
                noValidate
                data-testid="generator-form"
                className="mx-auto max-w-3xl rounded-[32px] border border-[var(--om-border)] bg-white p-8 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-12"
              >
                <div>
                  <label className="mb-3 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                    {t('generator.label_universe')}
                  </label>
                  <div className="flex flex-wrap gap-2" data-testid="generator-vertical-picker">
                    {VERTICALS.map((v) => {
                      const active = v.code === vertical;
                      const vc = verticalCopy(raw, v.code);
                      return (
                        <button
                          type="button"
                          key={v.code}
                          data-testid={`generator-vertical-${v.code}`}
                          onClick={() => { setVertical(v.code); if (errors.vertical) setErrors({ ...errors, vertical: '' }); }}
                          className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                            active
                              ? 'border-transparent text-white shadow-[0_14px_28px_-14px_rgba(10,14,26,0.4)]'
                              : 'border-[var(--om-border)] bg-white text-[var(--om-ink)] hover:bg-[var(--om-bg)]'
                          }`}
                          style={active ? { background: v.color } : undefined}
                        >
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ background: active ? '#fff' : v.color }}
                          />
                          {vc.short}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-8">
                  <label
                    htmlFor="generator-description"
                    className="mb-3 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]"
                  >
                    {t('generator.label_description')}
                  </label>
                  <textarea
                    id="generator-description"
                    value={description}
                    onChange={(e) => { setDescription(e.target.value.slice(0, MAX_LEN + 20)); if (errors.description) setErrors({ ...errors, description: '' }); }}
                    placeholder={placeholders[vertical] || ''}
                    rows={7}
                    data-testid="generator-description-input"
                    aria-invalid={errors.description ? 'true' : 'false'}
                    className={`w-full rounded-2xl border bg-white px-5 py-4 text-sm leading-relaxed text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] outline-none transition-all duration-200 focus:ring-2 focus:ring-black/10 ${
                      errors.description ? 'border-red-400 focus:border-red-500' : 'border-[var(--om-border)] focus:border-[var(--om-ink)]'
                    }`}
                  />
                  <div className="mt-2 flex items-center justify-between">
                    {errors.description ? (
                      <span className="text-xs text-red-600" data-testid="generator-description-error">
                        {errors.description}
                      </span>
                    ) : (
                      <span className="text-xs text-[var(--om-muted-2)]">
                        {t('generator.desc_hint', { min: MIN_LEN, max: MAX_LEN })}
                      </span>
                    )}
                    <span className={`text-xs tabular-nums ${remaining < 0 ? 'text-red-600' : 'text-[var(--om-muted-2)]'}`}>
                      {remaining}
                    </span>
                  </div>
                </div>

                {genError && (
                  <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700" data-testid="generator-error">
                    {genError}
                  </div>
                )}

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                  <div className="text-xs text-[var(--om-muted-2)]">
                    {t('generator.avg_time')}
                  </div>
                  <button
                    type="submit"
                    data-testid="generator-submit-btn"
                    className="om-btn-primary"
                    style={{
                      background: currentVertical?.color,
                      color: '#fff',
                      boxShadow: `0 16px 32px -14px ${currentVertical?.color}`,
                    }}
                  >
                    <Sparkles size={14} strokeWidth={2.6} />
                    {t('generator.submit')}
                  </button>
                </div>
              </form>
            </div>
          </section>
        )}

        {step === 'loading' && (
          <section className="pb-20">
            <div className="om-container">
              <div className="mx-auto max-w-2xl rounded-[32px] border border-[var(--om-border)] bg-white p-12 text-center shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)]" data-testid="generator-loading">
                <div
                  className="mx-auto grid h-16 w-16 place-items-center rounded-full text-white"
                  style={{
                    background: currentVertical?.color,
                    boxShadow: `0 20px 40px -18px ${currentVertical?.color}`,
                  }}
                >
                  <Sparkles size={26} strokeWidth={2.4} className="om-anim-halo" />
                </div>
                <h2 className="om-h1 mt-6 text-[26px] text-[var(--om-ink)]">
                  {t('generator.loading_title')}
                </h2>
                <ul className="mx-auto mt-8 max-w-md space-y-3 text-left" data-testid="generator-loading-steps">
                  {loadingSteps.map((label, i) => {
                    const done = i < loadingIdx;
                    const active = i === loadingIdx;
                    return (
                      <li
                        key={i}
                        className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm transition-all ${
                          done
                            ? 'border-emerald-200 bg-emerald-50/60 text-emerald-800'
                            : active
                            ? 'border-[var(--om-border)] bg-white text-[var(--om-ink)]'
                            : 'border-transparent bg-transparent text-[var(--om-muted-2)]'
                        }`}
                      >
                        {done ? (
                          <Check size={16} strokeWidth={2.6} className="text-emerald-600" />
                        ) : active ? (
                          <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--om-ink)] border-t-transparent" />
                        ) : (
                          <span className="inline-block h-3 w-3 rounded-full border border-[var(--om-border)]" />
                        )}
                        {label}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-8 text-xs text-[var(--om-muted-2)]">
                  {t('generator.loading_avg')}
                </div>
              </div>
            </div>
          </section>
        )}

        {step === 'result' && teaser && (
          <section className="pb-20">
            <div className="om-container">
              <div className="mx-auto max-w-3xl">
                <div
                  data-testid="generator-teaser"
                  className="relative overflow-hidden rounded-[32px] border border-[var(--om-border)] bg-white p-8 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-12"
                >
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -right-24 -top-24 h-[380px] w-[380px] rounded-full blur-3xl"
                    style={{ background: `radial-gradient(closest-side, ${currentVertical?.color}44, transparent 70%)` }}
                  />
                  <div className="relative flex items-center gap-2">
                    <span
                      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider"
                      style={{ color: currentVertical?.color, background: `${currentVertical?.color}18` }}
                    >
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: currentVertical?.color }} />
                      {currentVerticalCopy?.label}
                    </span>
                    <span className="ml-auto text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                      ⏱ {teaser.estimated_build_time}
                    </span>
                  </div>

                  <h2 className="relative mt-5 text-[28px] sm:text-[36px] font-bold tracking-tight text-[var(--om-ink)]" data-testid="generator-teaser-name">
                    {teaser.app_name}
                  </h2>
                  <p className="relative mt-3 text-base md:text-lg leading-relaxed text-[var(--om-ink-soft)]" data-testid="generator-teaser-pitch">
                    {teaser.pitch}
                  </p>

                  {!fullConcept && (
                    <div
                      className="relative mt-8 rounded-2xl border border-[var(--om-border)] bg-[var(--om-bg)] p-6"
                      data-testid="generator-unlock-gate"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="grid h-10 w-10 place-items-center rounded-2xl text-white"
                          style={{ background: currentVertical?.color }}
                        >
                          <Lock size={16} strokeWidth={2.4} />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-[var(--om-ink)]">
                            {t('generator.gate_title')}
                          </div>
                          <div className="text-xs text-[var(--om-muted)]">
                            {t('generator.gate_subtitle')}
                          </div>
                        </div>
                      </div>

                      <form onSubmit={handleUnlock} noValidate className="mt-5 flex flex-col gap-3 sm:flex-row" data-testid="generator-unlock-form">
                        <input
                          type="text"
                          inputMode="email"
                          value={email}
                          onChange={(e) => { setEmail(e.target.value); if (emailError) setEmailError(''); }}
                          placeholder={t('common.email_placeholder')}
                          data-testid="generator-unlock-email-input"
                          aria-invalid={emailError ? 'true' : 'false'}
                          className={`om-input flex-1 ${emailError ? 'border-red-400' : ''}`}
                          disabled={unlocking}
                        />
                        <button
                          type="submit"
                          disabled={unlocking}
                          data-testid="generator-unlock-submit-btn"
                          className="om-btn-primary shrink-0"
                          style={{
                            background: currentVertical?.color,
                            color: '#fff',
                            boxShadow: `0 14px 28px -14px ${currentVertical?.color}`,
                          }}
                        >
                          {unlocking ? t('generator.gate_submitting') : t('generator.gate_submit')}
                        </button>
                      </form>
                      {emailError && (
                        <div className="mt-2 pl-5 text-xs text-red-600" data-testid="generator-unlock-email-error">
                          {emailError}
                        </div>
                      )}
                      <div className="mt-4 flex items-center gap-2 text-xs text-[var(--om-muted-2)]">
                        <Check size={12} strokeWidth={2.6} className="text-emerald-600" />
                        {t('generator.gate_no_spam')}
                        <span className="ml-1"><WaitlistCounter /></span>
                      </div>
                    </div>
                  )}
                </div>

                {fullConcept && (
                  <div
                    data-testid="generator-full-concept"
                    className="mt-8 rounded-[32px] border border-[var(--om-border)] bg-white p-8 shadow-[0_30px_80px_-40px_rgba(10,14,26,0.25)] md:p-12"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                        style={{ background: 'rgba(16,185,129,0.14)' }}
                      >
                        <Check size={12} strokeWidth={2.6} /> {t('generator.unlocked_badge')}
                      </span>
                    </div>

                    <section className="mt-6" data-testid="generator-section-users">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('generator.section_users')}
                      </h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {fullConcept.target_users.map((u, i) => (
                          <span
                            key={i}
                            className="rounded-full border border-[var(--om-border)] bg-[var(--om-bg)] px-3 py-1.5 text-sm font-medium text-[var(--om-ink)]"
                          >
                            {u}
                          </span>
                        ))}
                      </div>
                    </section>

                    <section className="mt-8" data-testid="generator-section-modules">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('generator.section_modules', { count: fullConcept.modules.length })}
                      </h3>
                      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {fullConcept.modules.map((m, i) => (
                          <div key={i} className="rounded-2xl border border-[var(--om-border)] bg-white p-4">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-[var(--om-ink)]">{m.name}</span>
                              {m.ai_powered && (
                                <span
                                  className="rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider"
                                  style={{ color: currentVertical?.color, background: `${currentVertical?.color}18` }}
                                >
                                  {lang === 'en' ? 'AI' : 'IA'}
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs leading-relaxed text-[var(--om-muted)]">{m.description}</p>
                          </div>
                        ))}
                      </div>
                    </section>

                    <section className="mt-8" data-testid="generator-section-screens">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('generator.section_screens', { count: fullConcept.key_screens.length })}
                      </h3>
                      <ol className="mt-3 space-y-2">
                        {fullConcept.key_screens.map((s, i) => (
                          <li
                            key={i}
                            className="flex items-start gap-3 rounded-2xl border border-[var(--om-border)] bg-white p-4"
                          >
                            <span
                              className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[10px] font-bold text-white"
                              style={{ background: currentVertical?.color }}
                            >
                              {i + 1}
                            </span>
                            <div>
                              <div className="text-sm font-bold text-[var(--om-ink)]">{s.name}</div>
                              <div className="mt-0.5 text-xs text-[var(--om-muted)]">{s.description}</div>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </section>

                    <section className="mt-8" data-testid="generator-section-blocks">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('generator.section_blocks')}
                      </h3>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {fullConcept.nocode_blocks.map((b, i) => (
                          <span
                            key={i}
                            className="rounded-full px-3 py-1.5 text-xs font-semibold"
                            style={{
                              color: currentVertical?.color,
                              background: `${currentVertical?.color}18`,
                            }}
                          >
                            {b}
                          </span>
                        ))}
                      </div>
                    </section>

                    <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-[var(--om-border)] pt-8">
                      <div className="text-sm text-[var(--om-muted)]">
                        {t('generator.estimate_prefix')} <span className="font-semibold text-[var(--om-ink)]">{fullConcept.estimated_build_time}</span> {t('generator.estimate_suffix')}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <button
                          type="button"
                          onClick={async () => {
                            if (openingBuilder) return;
                            if (authLoading) return;
                            if (!user) {
                              toast(t('builder.open_in_builder_needs_login'), { icon: '🔒' });
                              navigate(`/connexion?next=${encodeURIComponent('/generateur')}`);
                              return;
                            }
                            if (!user.plan || user.plan === 'none') {
                              toast(t('builder.open_in_builder_needs_plan'), { icon: '🔒' });
                              navigate('/tarifs');
                              return;
                            }
                            setOpeningBuilder(true);
                            const res = await createApp({ from_concept_id: fullConcept.id, lang });
                            setOpeningBuilder(false);
                            if (res.ok) {
                              track('app_created', { lang, vertical: fullConcept.vertical });
                              navigate(`/studio/app/${res.app.id}`);
                            } else if (res.status === 403) {
                              toast.error(t('builder.errors.invalid'));
                            } else {
                              toast.error(t('builder.errors.create_failed'));
                            }
                          }}
                          disabled={openingBuilder}
                          className="om-btn-primary disabled:opacity-60"
                          data-testid="generator-open-builder-btn"
                        >
                          <LayoutTemplate size={14} strokeWidth={2.4} />
                          {openingBuilder ? t('builder.opening') : t('builder.open_in_builder')}
                          <ArrowRight size={14} strokeWidth={2.4} />
                        </button>
                        <Link
                          to={`/prototype/${teaser?.id || fullConcept?.id || ''}`}
                          className="om-btn-ghost"
                          data-testid="generator-build-prototype-btn"
                        >
                          <Sparkles size={14} strokeWidth={2.4} />
                          {t('prototype.build_cta')}
                        </Link>
                        <button
                          type="button"
                          onClick={handleReset}
                          data-testid="generator-regenerate-btn"
                          className="om-btn-ghost"
                        >
                          <RefreshCw size={14} strokeWidth={2.4} />
                          {t('generator.regenerate')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {samples.length > 0 && (
          <section className="pb-24 pt-8" data-testid="generator-samples">
            <div className="om-container">
              <div className="max-w-2xl">
                <span className="om-eyebrow">{t('generator.samples_eyebrow')}</span>
                <h2 className="om-h1 mt-5 text-[32px] sm:text-[40px] text-[var(--om-ink)]">
                  {t('generator.samples_title')}
                </h2>
              </div>
              <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
                {samples.map((c) => {
                  const v = VERTICALS_BY_CODE[c.vertical];
                  return (
                    <ConceptCard
                      key={c.id}
                      concept={c}
                      accentColor={v?.color || '#0A0E1A'}
                      dataTestId={`generator-sample-${c.id}`}
                    />
                  );
                })}
              </div>
              <div className="mt-10 flex justify-center">
                <Link
                  to="/applications"
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--om-border)] bg-white px-5 py-2.5 text-sm font-semibold text-[var(--om-ink)] hover:-translate-y-[1px] transition-all duration-200 hover:shadow-[0_10px_20px_-14px_rgba(10,14,26,0.25)]"
                >
                  {t('generator.samples_cta')} <ArrowRight size={14} strokeWidth={2.4} />
                </Link>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}
